/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { SectionName } from '../../../common/analytics/section-name';
import { useSectionReveal } from '../../../common/analytics/useSectionReveal';
import { SpacingWrapper } from '../SpacingWrapper';

import { AppsColumn } from './AppsColumn';
import { BrandColumn } from './BrandColumn';
import { CompanyColumn } from './CompanyColumn';
import { LearnColumn } from './LearnColumn';
import { ResourcesColumn } from './ResourcesColumn';

const styles = cssMap({
	footer: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#18191A',
	},
	container: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'stretch',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		gap: '95px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '132px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '120px',
		// Tablet (≤ 1024px): trim the desktop gutter.
		'@media (max-width: 64rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			paddingInline: '48px',
			gap: token('space.600'),
		},
		// Small tablet / large phone (≤ 768px): drop the gutter entirely
		// so SpacingWrapper's 32px is the only horizontal padding, and
		// allow columns to wrap onto multiple rows.
		'@media (max-width: 48rem)': {
			paddingInline: token('space.0'),
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			paddingBlock: '64px',
			gap: token('space.600'),
			flexWrap: 'wrap',
		},
		// Phone (≤ 480px): tighter vertical pad.
		'@media (max-width: 30rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			paddingBlock: '40px',
			gap: token('space.400'),
		},
	},
});

export const Footer = (): JSX.Element => {
	const { fireEvent } = useAnalyticsEvents();
	// `threshold: 0` so the Footer reveal — and its piggy-back
	// `track.home.viewedToBottom.twgMicrositeHome` event — fire the moment
	// any pixel of the Footer scrolls into view. The Footer is anchored at
	// the very bottom of the page, so this matches the spec's
	// "user reached the end" semantic; the legacy `0.5` threshold could
	// fail to fire on tall Footers / short viewports.
	const sectionRef = useSectionReveal<HTMLDivElement>(SectionName.Footer, {
		threshold: 0,
		onReveal: (authState) => {
			fireEvent('track.home.viewedToBottom.twgMicrositeHome', { authState });
		},
	});

	return (
		<div ref={sectionRef} css={styles.footer}>
			<SpacingWrapper>
				<div css={styles.container}>
					<BrandColumn />
					<CompanyColumn />
					<AppsColumn />
					<ResourcesColumn />
					<LearnColumn />
				</div>
			</SpacingWrapper>
		</div>
	);
}
